/**
 * routePrefetch.ts — Helper for looking up prefetch factories by href.
 *
 * Used by TopNav, TopNavMobile, and SideRail to resolve a route href to
 * a dynamic-import factory for usePrefetchRoute.
 *
 * @see specs/prd.md FR-021
 */
import { ROUTE_PREFETCH_MAP } from "./generatedRoutePrefetchMap";

// ---------------------------------------------------------------------------
// Noop factory — returned when no match is found
// ---------------------------------------------------------------------------

const NOOP_FACTORY: () => Promise<unknown> = () => Promise.resolve();

// ---------------------------------------------------------------------------
// Cache for stable factory references per href
// ---------------------------------------------------------------------------

const factoryCache = new Map<string, () => Promise<unknown>>();

/**
 * Look up the prefetch factory for a given route href.
 *
 * Returns a stable function reference (cached per href) so that
 * usePrefetchRoute's deduplication works correctly.
 */
export function getPrefetchFactory(href: string): () => Promise<unknown> {
  const cached = factoryCache.get(href);
  if (cached) return cached;

  // Normalise href (remove leading hash if present)
  const normalised = href.replace(/^#/, "");

  // Exact match
  const exact = ROUTE_PREFETCH_MAP[normalised];
  if (exact) {
    factoryCache.set(href, exact);
    return exact;
  }

  // Prefix match: find the longest matching route pattern
  let bestMatch = "";
  let bestFactory: (() => Promise<unknown>) | undefined;

  for (const [pattern, factory] of Object.entries(ROUTE_PREFETCH_MAP)) {
    const prefix = pattern
      .split("/")
      .filter((seg) => !seg.startsWith(":"))
      .join("/");
    if (normalised.startsWith(prefix) && prefix.length > bestMatch.length) {
      bestMatch = prefix;
      bestFactory = factory;
    }
  }

  const result = bestFactory ?? NOOP_FACTORY;
  factoryCache.set(href, result);
  return result;
}
